import { MDX } from "./mdx/tokens";
import { Bot, Sparkles, Check, Minus } from "lucide-react";

/**
 * AiCopilotComparison — Section 4.
 * Streamlined 10-feature matrix comparing:
 *   • Kimani (individual · rule-based)
 *   • Obondo (institutional · LLM-powered, fully autonomous with kill-switch)
 *
 * Copy is trader-friendly (no proprietary formulas). Obondo-only capabilities
 * are chipped so scanners can see the delta at a glance.
 */
const AI_COPILOT_FEATURES = [
    {
        feat:   "Checks Your Portfolio And Tells You What To Do",
        kimani: "A quick look at what you own",
        obondo: "Live check of your account value, with hold-or-sell tips and rebalancing help",
    },
    {
        feat:   "Personal Tutor For Trading And Money Basics",
        kimani: "Look up a word's meaning",
        obondo: "Ask questions and get answers about money, companies, and what you own",
    },
    {
        feat:   "Explains The Risk Before You Trade",
        kimani: "A short standard note",
        obondo: "A written explanation, plus a market-mood label",
    },
    {
        feat:   "Suggests When To Set A Safety Stop",
        kimani: "Reminds you to set a safety stop (a price where you auto-sell to limit a loss)",
        obondo: "Sets a safety stop sized to how fast the price is moving",
    },
    {
        feat:       "Explains Why A Trade Lost Money",
        kimani:     null,
        obondo:     "A full write-up of what went wrong and why",
        obondoOnly: true,
    },
    {
        feat:       "Can Trade For You, With An Easy Off-Switch",
        kimani:     "Gives advice only",
        obondo:     "Gives advice, helps you decide, or trades for you — always with an easy off-switch",
        obondoOnly: true,
    },
    {
        feat:       "Tests Your Idea On Past Prices First",
        kimani:     "Tests one idea at a time on past prices",
        obondo:     "Tests up to 3 ideas together and shows the risk",
        obondoOnly: true,
    },
    {
        feat:       "Checks A Company's Health And The News Buzz",
        kimani:     null,
        obondo:     "Looks at a company's numbers (like profit and dividends) and the news mood around it",
        obondoOnly: true,
    },
    {
        feat:       "Full Record For Schools And Firms",
        kimani:     null,
        obondo:     "A downloadable record for regulators, plus a group admin overview",
        obondoOnly: true,
    },
    {
        feat:   "Speaks Your Language",
        kimani: "🇬🇧 English · 🇰🇪 Kiswahili (set phrases)",
        obondo: "🇬🇧 English · 🇰🇪 Kiswahili · 🇰🇪 Sheng · 🇫🇷 French · 🇸🇦 Arabic · 🇵🇹 Portuguese — mixed naturally",
    },
];

export default function AiCopilotComparison({ isDark }) {
    const bg = isDark ? "bg-[#090D16]" : "bg-white";
    const text = isDark ? "text-[#F9FAFB]" : "text-[#090D16]";
    const dim = isDark ? "text-[#9CA3AF]" : "text-[#6B7280]";
    const brd = isDark ? "border-[#1F2937]" : "border-[#E5E7EB]";
    const cardBg = isDark ? "bg-[#0F1524]" : "bg-white";
    const rowAlt = isDark ? "bg-[#0B1120]" : "bg-[#F9FAFB]";

    return (
        <section
            className={`${bg} ${text} py-16 md:py-24 border-t ${brd}`}
            data-testid="ai-copilot-comparison"
        >
            <div className="max-w-[1200px] mx-auto px-4 md:px-8">
                <div className="mb-8">
                    <div className="font-inter text-xs font-bold uppercase tracking-widest mb-3" style={{ color: MDX.blueLow }}>
                        Your Learning Helper
                    </div>
                    <h2 className="font-inter font-medium leading-tight" style={{ fontSize: "28px" }} data-testid="ai-copilot-h2">
                        Meet Your Trading Helper
                    </h2>
                    <p
                        className={`font-roboto mt-3 leading-relaxed ${dim}`}
                        style={{ fontSize: "clamp(16px, 1.8vw, 18px)" }}
                        data-testid="ai-copilot-body"
                    >
                        Everyone gets a helper. You start free with <b style={{ color: MDX.blueLow }}>Kimani</b>.
                        <b className="text-[#006600] dark:text-[#4ADE80]"> When you're ready to go further, upgrade to Obondo</b> — a smarter helper that can act for you, always with an easy off-switch.
                    </p>
                </div>

                {/* Desktop table — hidden below lg to avoid cramped tablet layout */}
                <div className={`hidden lg:block rounded-xl border ${brd} ${cardBg} overflow-hidden`} data-testid="ai-copilot-table">
                    <table className="w-full text-left">
                        <thead>
                            <tr className={rowAlt}>
                                <th className="font-inter font-bold text-[13px] uppercase tracking-wider px-5 py-3 w-[38%]">
                                    <span className={dim}>What It Does</span>
                                </th>
                                <th className="font-inter font-bold text-[13px] uppercase tracking-wider px-5 py-3">
                                    <span className="inline-flex items-center gap-2" style={{ color: MDX.blueLow }}>
                                        <Bot className="h-4 w-4" />
                                        Kimani · Free
                                    </span>
                                </th>
                                <th className="font-inter font-bold text-[13px] uppercase tracking-wider px-5 py-3">
                                    <span className="inline-flex items-center gap-2 text-[#006600] dark:text-[#4ADE80]">
                                        <Sparkles className="h-4 w-4" />
                                        Obondo · Advanced &amp; Institutional
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {AI_COPILOT_FEATURES.map((r, i) => (
                                <tr key={r.feat} className={i % 2 === 1 ? rowAlt : ""} data-testid={`ai-copilot-row-${i}`}>
                                    <td className="font-inter font-semibold text-[15px] px-5 py-4 align-top">
                                        {r.feat}
                                    </td>
                                    <td className="font-roboto text-[15px] px-5 py-4 align-top">
                                        <KimaniCell text={r.kimani} dim={dim} />
                                    </td>
                                    <td className="font-roboto text-[15px] px-5 py-4 align-top">
                                        <ObondoCell text={r.obondo} obondoOnly={r.obondoOnly} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Tablet + mobile — stacked cards */}
                <div className="lg:hidden space-y-3">                    {AI_COPILOT_FEATURES.map((r, i) => (
                        <div
                            key={r.feat}
                            className={`rounded-xl border ${brd} ${cardBg} p-4`}
                            data-testid={`ai-copilot-card-${i}`}
                        >
                            <div className="font-inter font-bold text-[15px] leading-tight mb-3">{r.feat}</div>
                            <div className="grid grid-cols-1 gap-2">
                                <div>
                                    <div className="font-inter text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: MDX.blueLow }}>
                                        Kimani · Free
                                    </div>
                                    <div className={`font-roboto text-[15px] ${!r.kimani ? "opacity-60" : ""}`}>
                                        <KimaniCell text={r.kimani} dim={dim} />
                                    </div>
                                </div>
                                <div className="pt-2 border-t border-dashed" style={{ borderColor: isDark ? "#1F2937" : "#E5E7EB" }}>
                                    <div className="font-inter text-[10px] font-bold uppercase tracking-wider mb-1 text-[#006600] dark:text-[#4ADE80]">
                                        Obondo · Advanced &amp; Institutional
                                    </div>
                                    <div className="font-roboto text-[15px]">
                                        <ObondoCell text={r.obondo} obondoOnly={r.obondoOnly} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function KimaniCell({ text, dim }) {
    if (!text) {
        return (
            <span className={`inline-flex items-center gap-1.5 ${dim}`}>
                <Minus className="h-3.5 w-3.5" /> —
            </span>
        );
    }
    return (
        <span className="inline-flex items-start gap-2">
            <Check className="h-4 w-4 mt-0.5 shrink-0 opacity-70" style={{ color: MDX.blueLow }} />
            <span>{text}</span>
        </span>
    );
}

function ObondoCell({ text, obondoOnly }) {
    return (
        <div className="flex flex-col gap-1.5">
            <span className="inline-flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 shrink-0 text-[#006600] dark:text-[#4ADE80]" />
                <span>{text}</span>
            </span>
            {obondoOnly && (
                <span
                    className="self-start font-inter text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border bg-[rgba(0,102,0,0.10)] text-[#006600] border-[#006600] dark:bg-[rgba(0,102,0,0.25)] dark:text-[#4ADE80] dark:border-[#4ADE80]"
                >
                    Obondo Only
                </span>
            )}
        </div>
    );
}
